import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listTransfers, listTransferLines } from "@/lib/transfers.functions";
import { transferReceive } from "@/lib/transfer-reconcile.functions";
import { RfShell, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useScope } from "@/hooks/use-scope";
import { StatusPill } from "@/components/ui/status-pill";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/transfers")({
  component: RfTransfers,
});

function RfTransfers() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listTransfers);
  const linesFn = useServerFn(listTransferLines);
  const receiveFn = useServerFn(transferReceive);

  const { data: transfers = [], isLoading } = useQuery({
    queryKey: ["rf-transfers", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, sort: "desc" } }),
  });

  const inbound = useMemo(
    () => (transfers as any[]).filter(
      (t) => t.dest_facility_id === facilityId && t.status !== "received" && t.status !== "cancelled",
    ),
    [transfers, facilityId],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: lines = [], refetch: refetchLines } = useQuery({
    queryKey: ["rf-transfer-lines", activeId],
    enabled: !!activeId,
    queryFn: () => linesFn({ data: { id: activeId! } }),
  });

  const activeLine: any = useMemo(
    () => (lines as any[]).find((l) => l.id === activeLineId) ?? null,
    [lines, activeLineId],
  );

  if (!tenantId || !facilityId) {
    return <RfShell title="Transfers" back><RfScopeMissing /></RfShell>;
  }

  async function confirm() {
    if (!activeLine) return;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive qty");
    setBusy(true);
    try {
      await receiveFn({ data: { transferLineId: activeLine.id, qtyReceived: n, condition: "good" } });
      toast.success(`Received ${n}`);
      setActiveLineId(null); setQty("");
      await refetchLines();
      qc.invalidateQueries({ queryKey: ["rf-transfers"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Receive failed");
    } finally { setBusy(false); }
  }

  if (activeId && !activeLineId) {
    const transfer: any = (transfers as any[]).find((t) => t.id === activeId);
    return (
      <RfShell title={transfer?.reference ?? "Transfer"}>
        <Button variant="outline" className="w-full" onClick={() => setActiveId(null)}>← Back to transfers</Button>
        <div className="space-y-2">
          {(lines as any[]).length === 0 ? (
            <RfEmpty>No lines.</RfEmpty>
          ) : (
            (lines as any[]).map((l: any) => (
              <button key={l.id} type="button"
                onClick={() => { setActiveLineId(l.id); setQty(String(l.qty)); }}
                className="flex w-full items-center gap-3 rounded-md border bg-card px-3 py-3 text-left active:bg-muted/70">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-sm">{l.sku_code ?? l.sku_id?.slice(0, 8) ?? "—"}</div>
                  <div className="truncate text-xs text-muted-foreground">{l.sku_name ?? ""}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-base font-semibold tabular-nums">{l.qty}</div>
                  <div className="text-[10px] text-muted-foreground">shipped</div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
              </button>
            ))
          )}
        </div>
      </RfShell>
    );
  }

  if (activeLine) {
    return (
      <RfShell title="Receive Line">
        <div className="rounded-md border bg-muted/30 p-3 space-y-1">
          <div className="font-mono text-sm">{activeLine.sku_code ?? activeLine.sku_id?.slice(0, 8)}</div>
          <div className="text-sm">{activeLine.sku_name ?? ""}</div>
          <div className="text-xs text-muted-foreground">Shipped {activeLine.qty}</div>
        </div>
        <Input inputMode="numeric" pattern="[0-9]*" autoFocus value={qty}
          onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))}
          className="h-14 text-2xl text-center font-mono" />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => { setActiveLineId(null); setQty(""); }} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy || !qty}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm
          </Button>
        </div>
      </RfShell>
    );
  }

  return (
    <RfShell title="Transfers" back>
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${inbound.length} inbound`}
      </div>
      <div className="space-y-2">
        {inbound.length === 0 && !isLoading ? (
          <RfEmpty>No inbound transfers awaiting receipt.</RfEmpty>
        ) : (
          inbound.map((t: any) => (
            <button key={t.id} type="button" onClick={() => setActiveId(t.id)}
              className="flex w-full items-center gap-3 rounded-md border bg-card px-3 py-3 text-left active:bg-muted/70">
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-sm">{t.reference ?? t.id.slice(0, 8)}</div>
                <div className="text-xs text-muted-foreground">
                  {t.posted_at ? `Posted ${new Date(t.posted_at).toLocaleDateString()}` : "Not posted"}
                </div>
              </div>
              <StatusPill tone="info">{t.status}</StatusPill>
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            </button>
          ))
        )}
      </div>
    </RfShell>
  );
}